import { useState } from "react";
import { usePrompts } from "../hooks/usePrompts";
import type { Prompt, PromptCategoryWithPrompts } from "../hooks/usePrompts";
import styles from "./Prompts.module.css";

interface PromptFormState {
  title: string;
  description: string;
  category_id: string;
  template: string;
  sort_order: string;
}

const emptyForm: PromptFormState = {
  title: "",
  description: "",
  category_id: "",
  template: "",
  sort_order: "",
};

export default function Prompts() {
  const {
    categories,
    loading,
    createCategory,
    updateCategory,
    deleteCategory,
    createPrompt,
    updatePrompt,
    deletePrompt,
  } = usePrompts();

  const [filterCategoryId, setFilterCategoryId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [form, setForm] = useState<PromptFormState>(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const allPrompts = categories.flatMap((c) => c.prompts);
  const visiblePrompts =
    filterCategoryId === null
      ? allPrompts
      : allPrompts.filter((p) => p.category_id === filterCategoryId);

  function categoryName(id: number): string {
    return categories.find((c) => c.id === id)?.name ?? "—";
  }

  function openAddPrompt() {
    setEditingPrompt(null);
    setForm({
      ...emptyForm,
      category_id: filterCategoryId !== null ? String(filterCategoryId) : "",
    });
    setFormError("");
    setModalOpen(true);
  }

  function openEditPrompt(p: Prompt) {
    setEditingPrompt(p);
    setForm({
      title: p.title,
      description: p.description,
      category_id: String(p.category_id),
      template: p.template,
      sort_order: String(p.sort_order),
    });
    setFormError("");
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingPrompt(null);
    setForm(emptyForm);
    setFormError("");
  }

  async function handleSavePrompt() {
    if (!form.title.trim()) {
      setFormError("Title is required.");
      return;
    }
    if (!form.category_id) {
      setFormError("Category is required.");
      return;
    }
    setFormError("");
    setSaving(true);
    try {
      const data = {
        title: form.title.trim(),
        description: form.description.trim(),
        category_id: Number(form.category_id),
        template: form.template.trim(),
        sort_order: form.sort_order ? Number(form.sort_order) : undefined,
      };
      if (editingPrompt) {
        await updatePrompt(editingPrompt.id, data);
      } else {
        await createPrompt(data);
      }
      closeModal();
    } finally {
      setSaving(false);
    }
  }

  async function handleDeletePrompt(p: Prompt) {
    if (!confirm(`Delete "${p.title}"?`)) return;
    await deletePrompt(p.id);
  }

  async function handleAddCategory() {
    const name = prompt("Category name:");
    if (!name?.trim()) return;
    await createCategory(name.trim());
  }

  async function handleEditCategory(cat: PromptCategoryWithPrompts) {
    const name = prompt("Rename category:", cat.name);
    if (!name?.trim() || name.trim() === cat.name) return;
    await updateCategory(cat.id, name.trim());
  }

  async function handleDeleteCategory(cat: PromptCategoryWithPrompts) {
    const res = await deleteCategory(cat.id);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      alert((body as { error?: string }).error ?? "Cannot delete category.");
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.headerTitle}>Prompts Library</h1>
        <span className={styles.headerLine} />
        <div className={styles.headerActions}>
          <button className={styles.btnSecondary} onClick={handleAddCategory}>
            + Category
          </button>
          <button className={styles.btnPrimary} onClick={openAddPrompt}>
            + Prompt
          </button>
        </div>
      </div>

      {!loading && categories.length > 0 && (
        <div className={styles.categoryBar}>
          <button
            className={`${styles.categoryPill} ${filterCategoryId === null ? styles.categoryPillActive : ""}`}
            onClick={() => setFilterCategoryId(null)}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`${styles.categoryPill} ${filterCategoryId === cat.id ? styles.categoryPillActive : ""}`}
              onClick={() => setFilterCategoryId(cat.id)}
            >
              {cat.name}
              <span
                className={styles.categoryPillEditBtn}
                role="button"
                tabIndex={0}
                title="Edit category"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditCategory(cat);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    handleEditCategory(cat);
                  }
                }}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </span>
              <span
                className={styles.categoryPillEditBtn}
                role="button"
                tabIndex={0}
                title="Delete category"
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm(`Delete category "${cat.name}"?`))
                    handleDeleteCategory(cat);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    if (confirm(`Delete category "${cat.name}"?`))
                      handleDeleteCategory(cat);
                  }
                }}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4h6v2" />
                </svg>
              </span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className={styles.empty}>Loading…</p>
      ) : visiblePrompts.length === 0 ? (
        <p className={styles.empty}>
          No prompts yet. Click "+ Prompt" to add one.
        </p>
      ) : (
        <div className={styles.card}>
          <table className={styles.table}>
            <thead className={styles.tableHead}>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Template</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visiblePrompts.map((p) => (
                <tr key={p.id} className={styles.tableRow}>
                  <td>
                    <p className={styles.promptTitle}>{p.title}</p>
                    {p.description && (
                      <p className={styles.promptDesc}>{p.description}</p>
                    )}
                  </td>
                  <td>
                    <span className={styles.promptCategory}>
                      {categoryName(p.category_id)}
                    </span>
                  </td>
                  <td>
                    <span className={styles.promptTemplate}>
                      {p.template || "—"}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actionsCell}>
                      <button
                        className={styles.actionBtn}
                        title="Edit"
                        onClick={() => openEditPrompt(p)}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                        title="Delete"
                        onClick={() => handleDeletePrompt(p)}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M9 6V4h6v2" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>
              {editingPrompt ? "Edit Prompt" : "Add Prompt"}
            </h2>

            {formError && <p className={styles.formError}>{formError}</p>}

            <div className={styles.formField}>
              <label className={styles.formLabel}>Title</label>
              <input
                className={styles.formInput}
                value={form.title}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
                placeholder="e.g. Create email nurture sequence"
                autoFocus
              />
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel}>Description</label>
              <input
                className={styles.formInput}
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                placeholder="Brief description of what this prompt does"
              />
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel}>Category</label>
              <select
                className={styles.formSelect}
                value={form.category_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, category_id: e.target.value }))
                }
              >
                <option value="">Select a category…</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel}>Template</label>
              <textarea
                className={styles.formTextarea}
                value={form.template}
                onChange={(e) =>
                  setForm((f) => ({ ...f, template: e.target.value }))
                }
                placeholder="Enter the prompt template…"
              />
              <span className={styles.formHint}>
                Use {"{{portalId}}"} and {"{{portalName}}"} as dynamic
                placeholders.
              </span>
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel}>Sort order</label>
              <input
                className={styles.formInput}
                type="number"
                value={form.sort_order}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sort_order: e.target.value }))
                }
                placeholder="0"
              />
            </div>

            <div className={styles.modalActions}>
              <button className={styles.btnSecondary} onClick={closeModal}>
                Cancel
              </button>
              <button
                className={styles.btnPrimary}
                onClick={handleSavePrompt}
                disabled={saving}
              >
                {saving
                  ? "Saving…"
                  : editingPrompt
                    ? "Save changes"
                    : "Add prompt"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
